import {
  Box,
  Button,
  ButtonAppearance,
  ButtonComponentProps,
  ButtonSize,
  Modal,
  ModalHeader,
  ModalSize,
} from '@bechtel/gds';
import styled, { css } from 'styled-components';

export const rowHeight = 22;

export const TableStyled = styled.table`
  border-collapse: collapse;
  ${({ theme }) => css`
    font-size: ${theme.fontSizes[250]};
  `}
`;

export const TableHeaderStyled = styled.th.attrs({})`
  ${({ theme }) => css`
    padding: 7px 10px;
    border: 1px solid ${theme.colors.grey[200]};
    border-left: none;
    border-top: none;
  `}
`;

export const TableHeaderRowStyled = styled.tr`
  background: #f8f8f8; /* Matching ag-grid background color */
`;

interface TableDataProps {
  disabled?: boolean;
  selected?: boolean;
}

export const TableDataStyled = styled.td.attrs({})<TableDataProps>`
  position: relative;
  ${({ theme }) => css`
    border: 1px solid ${theme.colors.grey[200]};
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.colors.grey[100]};
      opacity: 0.9;
    `}

    ${({ selected }) =>
    selected &&
    css`
      background-color: ${({ theme }) => theme.colors.blue[200]};
      opacity: 0.9;
    `}
`;

interface TableBodyProps {
  rowCount: number;
}
export const TableBodyStyled = styled.tbody.attrs({})<TableBodyProps>`
  ${({ rowCount }) => css`
    height: ${rowHeight * rowCount}px;
  `}
`;

export const BreakerModalStyled = styled(Modal).attrs({
  size: ModalSize.DEFAULT,
})`
  width: 1200px;
  max-width: 80%;
  max-height: 80%;
`;

export const ModalFooterStyled = styled(Box).attrs({
  justifyContent: { sm: 'flex-end' },
})`
  background-color: ${({ theme }) => theme.colors.white};
  width: 100%;
`;

export const ModalHeaderStyled = styled(ModalHeader)`
  display: flex;
  align-items: center;
  margin: 0;
  ${({ theme }) => css`
    padding: ${theme.spacings[200]};
    padding-left: ${theme.spacings[500]};
    height: ${theme.spacings[800]};
  `}
`;
interface HorizontalLineProps {
  topOffsetIndex: number;
}

export const HorizontalLineStyled = styled.div.attrs({})<HorizontalLineProps>`
  ${({ topOffsetIndex }) => css`
    width: 100%;
    background: black;
    height: 0.5px; /* 22px per row */
    position: absolute;
    left: 0px;
    top: ${rowHeight / 2 + topOffsetIndex * rowHeight}px;
  `}
`;

interface VerticalLineProps {
  leftOffset: number;
  rowCount: number;
}

export const VerticalLineStyled = styled.div.attrs({})<VerticalLineProps>`
  ${({ leftOffset, rowCount }) => css`
    width: 0.5px;
    background: black;
    height: ${rowCount * rowHeight}px; /* 22px per row */
    position: absolute;
    left: ${leftOffset}px;
  `}
`;

interface MarkerProps {
  markerIndex: number;
  leftOffset: number;
}
export const MarkerStyled = styled.div.attrs({})<MarkerProps>`
  ${({ markerIndex, leftOffset: leftIndex }) => css`
    width: 7px;
    background: black;
    height: 7px;
    position: absolute;
    left: ${leftIndex}px;
    top: ${8 + markerIndex * rowHeight}px;
  `}
`;

interface ButtonProps {
  secondary?: boolean;
}

export const ButtonStyled = styled(Button).attrs<ButtonProps>((props) => ({
  appearance: props.secondary ? ButtonAppearance.SECONDARY : ButtonAppearance.PRIMARY,
  size: ButtonSize.SMALL,
}))<ButtonComponentProps & ButtonProps>`
  ${({ theme, secondary }) => css`
    width: 100px;
    height: 36px;
    margin-left: ${!secondary && theme.spacings[400]};
    padding: 0 ${theme.spacings[500]};
    justify-content: space-evenly;
  `}
`;

export const GridAreaStyled = styled.div`
  overflow-x: auto;
  display: flex;
  flex-grow: 1;
  ${({ theme }) => css`
    padding-left: ${theme.spacings[500]};
    padding-right: ${theme.spacings[500]};
  `}
`;

export const PanelSectionStyled = styled(Box).attrs({
  mt: { sm: '500' },
})`
  border: 1px solid #babfc7; /* Matching ag-grid border color */
  height: 100%;
`;

interface GridWrapperStyleProps {
  fontSize: string | number;
  headerFontSize?: number;
  mixedCaseFilter?: boolean;
}

export const GridWrapperStyled = styled.div.attrs({
  className: 'ag-theme-alpine',
})<GridWrapperStyleProps>`
  height: 100%;
  flex-shrink: 0;
  z-index: 0;
  ${({ theme, fontSize, headerFontSize, mixedCaseFilter }) => css`
    .ag-text-field-input {
      text-transform: ${!mixedCaseFilter && 'uppercase'};
    }

    .ag-header-cell-text {
      white-space: pre-line;
      font-size: ${headerFontSize ? theme.fontSizes[headerFontSize] : theme.fontSizes[250]};
    }

    .ag-header-cell-label {
      justify-content: center;
    }

    .ag-header-group-cell-with-group {
      justify-content: center;
      font-size: ${theme.fontSizes[250]};
    }

    .ag-root-wrapper {
      border-radius: 0px;
      border: none;
    }

    .ag-cell-focus {
      //Disable border for cell focus, we want the border to be shown for the whole row
      border: none !important;
    }

    .ag-row-focus {
      font-weight: ${theme.fontWeights[600]};
      z-index: 1;
    }

    .ag-row-selected .ag-cell {
      background: ${theme.colors.primary[200]};
    }

    .ag-cell {
      border-right-color: ${theme.colors.grey[200]};
      border-right-width: 1px;
      border-right-style: solid;
    }

    .ag-sticky-bottom {
      display: none;
    }

    .ag-row:last-child {
      border: none;
    }

    .ag-row .ag-cell:last-child {
      border: none;
    }

    .ag-header-row .ag-header-cell:last-child {
      border: none;
    }

    font-family: ${theme.fontFamilies.openSans};

    --ag-input-focus-border-color: ${theme.colors.primary[100]};
    --ag-checkbox-checked-color: ${theme.colors.primary[700]};
    --ag-font-size: ${fontSize};
    --ag-header-column-separator-display: block;
    --ag-header-column-separator-height: 100%;
    --ag-header-column-separator-width: 1px;
    --ag-header-column-separator-color: ${theme.colors.grey[200]};

    --ag-header-column-resize-handle-display: block;
    --ag-header-column-resize-handle-height: 100%;
    --ag-header-column-resize-handle-width: 1px;
    --ag-header-column-resize-handle-color: ${theme.colors.grey[200]};
    --ag-header-cell-horizontal-padding: 0 !important;
    --ag-cell-horizontal-padding: ${theme.spacings[300]};
  `};
`;
